import { useState, useEffect } from "react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";

const PCM_TYPES = ["Analyseur", "Empathique", "Persévérant", "Énergiseur", "Promoteur", "Imagineur"];

// Official PCM colors
const PCM_COLORS = {
  Analyseur: "#32A2FF",
  Empathique: "#FC7403",
  Persévérant: "#7F379D",
  Énergiseur: "#FAE200",
  Promoteur: "#FF1933",
  Imagineur: "#E3A88D",
};

/**
 * PCMFloorsEditor - Admin component to edit PCM floor percentages
 * @param {Object} pcmFloors - Current PCM floors data
 * @param {string} pcmBase - Current base type
 * @param {string} pcmPhase - Current phase type
 * @param {Function} onChange - Callback when data changes: (pcmFloors, pcmBase, pcmPhase) => void
 */
const PCMFloorsEditor = ({ pcmFloors = {}, pcmBase = "", pcmPhase = "", onChange }) => {
  const [floors, setFloors] = useState({});
  const [base, setBase] = useState("");
  const [phase, setPhase] = useState("");

  useEffect(() => {
    // Initialize floors with default values if not set
    const initialFloors = {};
    PCM_TYPES.forEach(type => {
      initialFloors[type] = pcmFloors?.[type] || 0;
    });
    setFloors(initialFloors);
    setBase(pcmBase || "");
    setPhase(pcmPhase || "");
  }, [pcmFloors, pcmBase, pcmPhase]);

  const handlePercentChange = (type, value) => {
    const numValue = Math.max(0, Math.min(100, parseInt(value) || 0));
    const newFloors = { ...floors, [type]: numValue };
    setFloors(newFloors);
    onChange?.(newFloors, base, phase);
  };

  const handleBaseChange = (type, checked) => {
    const newBase = checked ? type : "";
    setBase(newBase);
    onChange?.(floors, newBase, phase);
  };

  const handlePhaseChange = (type, checked) => {
    const newPhase = checked ? type : "";
    setPhase(newPhase);
    onChange?.(floors, base, newPhase);
  };

  // Calculate total to show warning if not 100%
  const total = Object.values(floors).reduce((sum, v) => sum + (v || 0), 0);

  return (
    <div className="space-y-4" data-testid="pcm-floors-editor">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-slate-700">
          Profil PCM — Immeuble
        </Label>
        {total > 0 && total !== 100 && (
          <span className={`text-xs px-2 py-1 rounded ${total < 100 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
            Total: {total}% {total < 100 ? "(< 100%)" : "(> 100%)"}
          </span>
        )}
        {total === 100 && (
          <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">
            Total: 100% ✓
          </span>
        )}
      </div>

      <div className="grid gap-3">
        {PCM_TYPES.map((type) => (
          <div 
            key={type} 
            className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            {/* Color indicator */}
            <div 
              className="w-3 h-8 rounded-full flex-shrink-0"
              style={{ backgroundColor: PCM_COLORS[type] }}
            />
            
            {/* Type name */}
            <span className="text-sm font-medium text-slate-700 w-24 flex-shrink-0">
              {type}
            </span>
            
            {/* Percentage input */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <Input
                type="number"
                min="0"
                max="100"
                value={floors[type] || 0}
                onChange={(e) => handlePercentChange(type, e.target.value)}
                className="w-16 h-8 text-center text-sm"
                data-testid={`pcm-percent-${type}`}
              />
              <span className="text-xs text-slate-400">%</span>
            </div>
            
            {/* Checkboxes */}
            <div className="flex items-center gap-4 ml-auto">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <Checkbox
                  checked={base === type}
                  onCheckedChange={(checked) => handleBaseChange(type, checked)}
                  className="border-[#FF6B35] data-[state=checked]:bg-[#FF6B35]"
                  data-testid={`pcm-base-${type}`}
                />
                <span className="text-xs text-slate-600">Base</span>
              </label>
              
              <label className="flex items-center gap-1.5 cursor-pointer">
                <Checkbox
                  checked={phase === type}
                  onCheckedChange={(checked) => handlePhaseChange(type, checked)}
                  className="border-[#7FD858] data-[state=checked]:bg-[#7FD858]"
                  data-testid={`pcm-phase-${type}`}
                />
                <span className="text-xs text-slate-600">Phase</span>
              </label>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400">
        La Base sera toujours affichée en bas de l'immeuble. Les autres étages sont triés par pourcentage.
      </p>
    </div>
  );
};

export default PCMFloorsEditor;
